import { isNative, bridgeRequest } from '@/shared/bridge';
import { deployUrl } from '@/shared/navigation';
// 매니페스트 형태 SSOT 는 생성기 쪽(`versions/version.type.ts`) — `scripts/generate-version.ts` 가 이 타입으로
// `public/version.json` 을 굽는다. src 밖이라 `@/` alias 가 닿지 않아 상대 경로로 가져온다(타입 전용 import).
import type { VersionManifest } from '../../../versions/version.type';

/** 배포 산출물 경로. `public/version.json` → 배포 도메인 루트. */
export const VERSION_MANIFEST_PATH = '/version.json';

/**
 * 배포된 버전 매니페스트 조회. **web·native 로 경로가 갈린다**:
 *
 * - **web 빌드** — SPA 와 `version.json` 을 같은 도메인이 서빙하므로 same-origin 상대 경로 fetch.
 *   절대 URL(`deployUrl`)을 쓰면 다른 도메인에서 서빙될 때 불필요하게 cross-origin 이 된다.
 * - **native 빌드** — WebView origin 은 `app://app.heviton.local`(iOS) / `https://app.heviton.local`(Android)
 *   이고 매니페스트는 배포 도메인에 있어 cross-origin. 정적 호스트가 CORS 헤더를 주지 않으면 WebView fetch 는
 *   차단되므로(그리고 `mode:'no-cors'` 는 본문을 못 읽음) **bridge `version.fetch` 로 native 에 위임**한다.
 *   native fetch 는 CORS 주체가 아니라 통과 — 응답은 본문 문자열, 파싱·검증은 여기서.
 *
 * 실패는 전부 throw — 호출측이 **fail-open** 으로 감싼다(버전 확인 실패가 앱 사용을 막아선 안 된다).
 * 구버전 앱(bridge v1)은 `version.fetch` 를 모르므로 native 가 ok:false 로 답한다 → 역시 throw → fail-open.
 */
export async function fetchVersionManifest(): Promise<VersionManifest> {
  const body = isNative() ? await fetchViaNativeBridge() : await fetchSameOrigin();
  return parseManifest(body);
}

/**
 * 캐시 무효화 쿼리(`?t=<epoch ms>`). 매 호출 URL 이 달라져 **어떤 캐시 계층도 재사용할 수 없다**.
 *
 * 요청 헤더(`no-store`/`no-cache`)만으로는 부족하다 — 그건 클라이언트 캐시에만 확실히 듣고, 중간 CDN·프록시가
 * 이미 물고 있는 사본은 그대로 돌려줄 수 있다. 특히 native 는 플랫폼 HTTP 캐시가 헤더를 무시하는 경우가 있어
 * URL 을 바꾸는 쪽이 유일하게 확실하다. 배포 직후 구버전 매니페스트를 읽으면 업데이트 안내가 아예 안 뜬다.
 */
function manifestPathWithCacheBuster(): string {
  return `${VERSION_MANIFEST_PATH}?t=${Date.now()}`;
}

async function fetchSameOrigin(): Promise<string> {
  const res = await fetch(manifestPathWithCacheBuster(), { cache: 'no-store' });
  if (!res.ok) throw new Error(`version manifest HTTP ${res.status}`);
  return res.text();
}

function fetchViaNativeBridge(): Promise<string> {
  // native 자체 타임아웃(4s)보다 넉넉히 — 먼저 끊기는 쪽이 native 여야 실패 사유가 구체적으로 남는다.
  return bridgeRequest<string>(
    { type: 'version.fetch', payload: { url: deployUrl(manifestPathWithCacheBuster()) } },
    { timeoutMs: 6000 },
  );
}

/**
 * 본문 문자열 → `VersionManifest`. 외부 정적 파일이라 **형태를 신뢰하지 않는다** — 배포 누락 시 정적 호스트가
 * SPA 폴백으로 `index.html` 을 주는 구성이면 200 + HTML 이 오기도 한다. 필드가 하나라도 어긋나면 throw 해서
 * 호출측 fail-open 으로 떨어뜨린다(반쪽 매니페스트로 강제 업데이트 판정을 내리는 쪽이 더 위험).
 *
 * 폼 입력이 아니라 zod 를 쓰지 않는다 — `shared/data-schemas` 는 폼 validation 전용 경계.
 */
function parseManifest(body: string): VersionManifest {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    throw new Error('version manifest JSON 파싱 실패');
  }
  if (!isRecord(parsed)) throw new Error('version manifest 형태 불일치: object 아님');

  return {
    web: parsePlatform(parsed.web, 'web'),
    native: parsePlatform(parsed.native, 'native'),
  };
}

function parsePlatform(value: unknown, key: string): VersionManifest['web'] {
  if (
    !isRecord(value) ||
    typeof value.latestVersion !== 'string' ||
    typeof value.minimumVersion !== 'string'
  ) {
    throw new Error(`version manifest 형태 불일치: ${key}`);
  }
  return { latestVersion: value.latestVersion, minimumVersion: value.minimumVersion };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
